import * as React from "react"
import { cn } from "../../lib/utils"
import { CalendarIcon, X, ChevronDownIcon } from "lucide-react"
import { Button } from "./button"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"
import { Calendar } from "./calendar"
import { Label } from "./label"
import { Switch } from "./switch"

export interface DateRangePickerProps {
  onUpdate?: (values: {
    dateFrom: Date | undefined
    dateTo: Date | undefined
    compareFrom: Date | undefined
    compareTo: Date | undefined
  }) => void
  initialDateFrom?: Date | string
  initialDateTo?: Date | string
  initialCompareFrom?: Date | string
  initialCompareTo?: Date | string
  align?: "start" | "center" | "end"
  locale?: string
  showCompare?: boolean
  className?: string
}

const DateRangePicker = React.forwardRef<HTMLDivElement, DateRangePickerProps>(
  ({
    onUpdate,
    initialDateFrom,
    initialDateTo,
    initialCompareFrom,
    initialCompareTo,
    align = "end",
    locale = "en-US",
    showCompare = true,
    className,
  }, ref) => {
    const [dateFrom, setDateFrom] = React.useState<Date | undefined>(
      initialDateFrom ? new Date(initialDateFrom) : undefined
    )
    const [dateTo, setDateTo] = React.useState<Date | undefined>(
      initialDateTo ? new Date(initialDateTo) : undefined
    )
    const [compareFrom, setCompareFrom] = React.useState<Date | undefined>(
      initialCompareFrom ? new Date(initialCompareFrom) : undefined
    )
    const [compareTo, setCompareTo] = React.useState<Date | undefined>(
      initialCompareTo ? new Date(initialCompareTo) : undefined
    )
    const [isCompare, setIsCompare] = React.useState(false)

    // Sync internal state with external props
    React.useEffect(() => {
      setDateFrom(initialDateFrom ? new Date(initialDateFrom) : undefined);
      setDateTo(initialDateTo ? new Date(initialDateTo) : undefined);
    }, [initialDateFrom, initialDateTo]);

    React.useEffect(() => {
      if (onUpdate) {
        onUpdate({
          dateFrom,
          dateTo,
          compareFrom: isCompare ? compareFrom : undefined,
          compareTo: isCompare ? compareTo : undefined,
        })
      }
    }, [dateFrom, dateTo, compareFrom, compareTo, isCompare])

    const formatDate = (date: Date | undefined) => {
      if (!date) return ""
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      return `${day}-${month}-${year} ${displayHours}:${minutes} ${ampm}`;
    }

    const getDateRangeText = () => {
      if (!dateFrom && !dateTo) return "Select date range"
      if (dateFrom && !dateTo) return `From ${formatDate(dateFrom)}`
      if (dateFrom && dateTo) {
        return `${formatDate(dateFrom)} - ${formatDate(dateTo)}`
      }
      return "Select date range"
    }

    const handleClear = () => {
      setDateFrom(undefined)
      setDateTo(undefined)
      setCompareFrom(undefined)
      setCompareTo(undefined)
      setIsCompare(false)
    }

    return (
      <div className={cn("relative", className)} ref={ref}>
        <Popover>
          <PopoverTrigger>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal",
                "h-9 rounded-md border border-[#E2E5EA] bg-white px-3 py-2 text-[13px]",
                "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-200 focus-visible:ring-offset-0",
                className
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              <span className="flex-1">{getDateRangeText()}</span>
              <ChevronDownIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align={align}>
            <div className="p-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Date Range</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="date-from" className="text-xs">From</Label>
                                             <input
                         type="date"
                         id="date-from"
                         aria-label="Select start date"
                         title="Select start date"
                         value={dateFrom?.toISOString().split('T')[0] || ''}
                         onChange={(e) => setDateFrom(e.target.value ? new Date(e.target.value) : undefined)}
                         className={cn(
                           "flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs",
                           "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-200 focus-visible:ring-offset-0"
                         )}
                       />
                    </div>
                    <div>
                      <Label htmlFor="date-to" className="text-xs">To</Label>
                                             <input
                         type="date"
                         id="date-to"
                         aria-label="Select end date"
                         title="Select end date"
                         value={dateTo?.toISOString().split('T')[0] || ''}
                         onChange={(e) => setDateTo(e.target.value ? new Date(e.target.value) : undefined)}
                         className={cn(
                           "flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs",
                           "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-200 focus-visible:ring-offset-0"
                         )}
                       />
                    </div>
                  </div>
                </div>

                {showCompare && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="compare-mode"
                        checked={isCompare}
                        onCheckedChange={setIsCompare}
                      />
                      <Label htmlFor="compare-mode" className="text-sm">Compare to previous period</Label>
                    </div>
                    {isCompare && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label htmlFor="compare-from" className="text-xs">Compare From</Label>
                                                     <input
                             type="date"
                             id="compare-from"
                             aria-label="Select compare start date"
                             title="Select compare start date"
                             value={compareFrom?.toISOString().split('T')[0] || ''}
                             onChange={(e) => setCompareFrom(e.target.value ? new Date(e.target.value) : undefined)}
                             className={cn(
                               "flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs",
                               "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-200 focus-visible:ring-offset-0"
                             )}
                           />
                        </div>
                        <div>
                          <Label htmlFor="compare-to" className="text-xs">Compare To</Label>
                                                     <input
                             type="date"
                             id="compare-to"
                             aria-label="Select compare end date"
                             title="Select compare end date"
                             value={compareTo?.toISOString().split('T')[0] || ''}
                             onChange={(e) => setCompareTo(e.target.value ? new Date(e.target.value) : undefined)}
                             className={cn(
                               "flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs",
                               "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-200 focus-visible:ring-offset-0"
                             )}
                           />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClear}
                    className="text-xs"
                  >
                    Clear
                  </Button>
                  <Button size="sm" className="text-xs">
                    Apply
                  </Button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    )
  }
)

DateRangePicker.displayName = "DateRangePicker"

export { DateRangePicker } 